import { MoUtils } from "ui-common";
import { h } from '@stencil/core';
import { ASIntegratorConfig, ASIntegratorLwTemplateData, ASIntegratorLwTemplateElement, ASIntegratorTemplateState, ASIntegratorTemplateVersionInfo } from "./interfaces";
import { getASIntegratorTemplateScriptSrc, fetchLatestTemplateVersion } from "./EmbedsASIntegratorHelpers";

export class EmbedsASIntegrator {
  private config: ASIntegratorConfig;
  private state: ASIntegratorTemplateState;
  private attemptTemplateInitialization: boolean = false;

  private templateVersionInfo?: ASIntegratorTemplateVersionInfo;

  private onDataProcessed?: (event: CustomEvent<ASIntegratorLwTemplateData | null>) => void;
  private onLoadingStateChange?: (isLoading: boolean) => void;
  private onError?: (error: string) => void;

  constructor(config: ASIntegratorConfig) {
    this.config = config;
    this.state = {
      loading: false,
      error: '',
      inputWarning: ''
    };

    if (this.config.template) {
      this.attemptTemplateInitialization = true;
    }
  }


  updateConfig(updatedConfig: Partial<ASIntegratorConfig>): void {
    this.config = { ...this.config, ...updatedConfig };
  }

  setOnDataProcessed(handler: (event: CustomEvent<ASIntegratorLwTemplateData | null>) => void): void {
    this.onDataProcessed = handler;
  }
  setOnLoadingStateChange(handler: (isLoading: boolean) => void): void {
    this.onLoadingStateChange = handler;
  }
  setOnError(handler: (error: string) => void): void {
    this.onError = handler;
  }

  getState(): ASIntegratorTemplateState {
    return { ...this.state };
  }

  getTemplateVersionInfo(): ASIntegratorTemplateVersionInfo | undefined {
    return this.templateVersionInfo ? { ...this.templateVersionInfo } : undefined;
  }

  setLoadingState(isLoading: boolean): void {
    this.state.loading = isLoading;
  }

  setErrorState(error: string): void {
    this.state.error = error;
    if (this.onError) {
      this.onError(error);
    }
  }

  resetTemplateState(): void {
    this.state.loading = false;
    this.state.error = '';
    this.state.inputWarning = '';
    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(false);
    }
  }

  isReadyToInitialize(): boolean {
    return this.attemptTemplateInitialization && !!this.config.template;
  }

  async handleTemplateInitialization(): Promise<any> {
    if (this.isReadyToInitialize()) {
      await MoUtils.waitForNextTick();
      this.attemptTemplateInitialization = false;

      this.resetTemplateState();

      const canInitializeTemplate = !!(this.config.template?.id);

      if (canInitializeTemplate) {
        try {
          await MoUtils.waitForNextTick();
          await this.createTemplate();
          return Promise.resolve();
        } catch (error) {
          return Promise.reject(error);
        }
      } else {
        return Promise.reject(new Error('Template ID is required to initialize the template.'));
      }
    } else {
      return Promise.reject(new Error('Integrator is not ready to initialize the template. Ensure that the template configuration is set and initialization has been requested.'));
    }
  }

  /**
   * Fetches the latest version of a template if useLatestVersion is enabled.
   * Stores both initialId and latestId for reference.
   * @returns The template ID to use for rendering (latest or original)
   */
  private async getTemplateIdToRender(): Promise<string> {
    const templateId = this.config.template?.id;
    const useLatest = this.config.template?.useLatestVersion;

    // If useLatestVersion is not explicitly true, or no templateId exists, use original ID
    if (useLatest !== true || !templateId) {
      return templateId || '';
    }

    try {
      const latestTemplate = await fetchLatestTemplateVersion(
        templateId,
        this.config.apiUrl,
        this.config.getBearerToken
      );

      this.templateVersionInfo = {
        initialId: latestTemplate.initialId || templateId,
        latestId: latestTemplate.id,
        useLatest
      };

      return latestTemplate.id;
    } catch (error) {
      console.error('Failed to fetch latest template version, using original ID:', error);
      return templateId;
    }
  }

  async createTemplate(): Promise<void> {
    this.cleanupTemplate();
    await MoUtils.waitForNextTick();

    if (this.validateTemplatePrerequisites()) {
      this.state.loading = true;
      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(true);
      }

      if (this.config.template.id === 'test-error') {
        await MoUtils.waitForNextTick();
        const errorMessage = 'An error occurred. Please verify the template ID and try again.';
        this.state.loading = false;
        this.state.error = errorMessage;
        if (this.onLoadingStateChange) {
          this.onLoadingStateChange(false);
        }
        if (this.onError) {
          this.onError(errorMessage);
        }
        return;
      }

      await this.initializeTemplateElements();
    }
  }

  private validateTemplatePrerequisites(): boolean {
    if (!this.config.hostElement) {
      this.state.loading = false;
      this.state.error = 'Template container not ready. Please try again.';
      return false;
    }

    if (!this.config.template) {
      this.state.loading = false;
      this.state.error = 'Cannot create template: No template selected.';
      return false;
    }

    if (!document.body.contains(this.config.hostElement)) {
      this.state.loading = false;
      this.state.error = 'Template container error (detached from DOM). Please try refreshing.';
      return false;
    }

    return true;
  }

  async initializeTemplateElements(): Promise<void> {
    const apiUrl = this.config.apiUrl || '';
    const embedToken = this.config.embedToken || '';

    try {
      await this.createScriptElement(apiUrl, embedToken);

      // Fetch latest version after script is loaded (if configured)
      const templateIdToRender = await this.getTemplateIdToRender();

      this.createTemplateElement(templateIdToRender);
    } catch (error) {
      await MoUtils.waitForNextTick();
      this.state.loading = false;
      this.state.error = `Failed to create template: ${error.message || 'Unknown error'}`;
    }
  }

  private createScriptElement(apiUrl: string, embedToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'lw-ui-lib';
      script.type = 'module';
      script.src = getASIntegratorTemplateScriptSrc();
      script.crossOrigin = 'anonymous';
      script.setAttribute('api-url', apiUrl);
      script.setAttribute('embed-token', embedToken);
      script.setAttribute('initiator', 'AS');

      script.onload = async () => {
        await MoUtils.waitForNextTick();
        resolve();
      };

      script.onerror = async () => {
        await MoUtils.waitForNextTick();
        this.state.loading = false;
        this.state.error = 'Failed to load template script.';
        console.error(this.state.error);
        if (this.onError) {
          this.onError(this.state.error);
        }
        reject(new Error(this.state.error));
      };

      this.config.hostElement.appendChild(script);
    });
  }

  createTemplateElement(templateId?: string): void {
    const lwTemplate = document.createElement('lw-template') as ASIntegratorLwTemplateElement;

    lwTemplate.id = templateId || this.config.template?.id || '';

    if (this.config.template?.layout) {
      lwTemplate.setAttribute('layout', this.config.template.layout);
    }

    if (this.config.template?.mockMode) {
      lwTemplate.setAttribute('mock-mode', 'true');
    }

    if (this.onDataProcessed) {
      lwTemplate.addEventListener('dataProcessed', this.onDataProcessed);
    }

    this.config.hostElement.appendChild(lwTemplate);
  }

  cleanupTemplate(): void {
    if (this.config.hostElement) {
      const lwTemplate = this.config.hostElement.querySelector('lw-template');
      if (lwTemplate && this.onDataProcessed) {
        lwTemplate.removeEventListener('dataProcessed', this.onDataProcessed);
      }
      while (this.config.hostElement.firstChild) {
        this.config.hostElement.firstChild.remove();
      }
    }
    this.resetTemplateState();
  }

  cleanup() {
    if (this.attemptTemplateInitialization || this.config.hostElement?.firstChild) {
      this.cleanupTemplate();
    }
    this.onDataProcessed = undefined;
    this.onLoadingStateChange = undefined;
    this.onError = undefined;
    this.attemptTemplateInitialization = false;
  }

  renderTemplate() {
    if (!this.config.hostElement) {
      console.error('Host element is not defined. Cannot render template.');
      return null;
    }

    const showActualTemplateContent = !this.state.loading && !this.state.error && !this.state.inputWarning;

    const children = [
      h('div', { className: 'template-content-wrapper' }, [
        h('div', { className: 'template-content-container' }, [
          showActualTemplateContent ? h('div', {
            className: 'manual-content-host',
            ref: (el: HTMLDivElement) => this.config.hostElement = el
          }) : null
        ].filter(Boolean))
      ]),
      this.state.loading ? h('div', { className: 'template-loading-overlay' }, [
        h('div', { className: 'spinner' })
      ]) : null,
      (!this.state.loading && this.state.error && !this.state.inputWarning) ? h('div', {
        className: 'template-error-message-overlay'
      }, [
        h('div', { className: 'error-icon' }),
        h('div', { className: 'template-error-text' }, this.state.error),
        h('div', { className: 'template-error-subtext' }, [
          h('p', null, 'Check the console for more details.')
        ])
      ]) : null
    ].filter(Boolean);

    return h('div', { className: 'template-container' }, children);
  }
}

